import { isArray, snakeCase } from 'lodash-es';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { GlintsTrackingClient } from './GlintsTrackingClient';
import { GLINTS_TRACKING_EVENT_QUEUE_KEY, GlintsTrackingEvent } from './types';

export function getEventQueue(): GlintsTrackingEvent[] {
  try {
    const queue = localStorage.getItem(GLINTS_TRACKING_EVENT_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch {
    return [];
  }
}

export function setEventQueue(queue: GlintsTrackingEvent[]) {
  try {
    localStorage.setItem(
      GLINTS_TRACKING_EVENT_QUEUE_KEY,
      JSON.stringify(queue)
    );
  } catch {
    return;
  }
}

export function trackEvent(
  event: Pick<GlintsTrackingEvent, 'event_type' | 'properties'>
) {
  if (!window?.GlintsTracking) return;
  const trackingEvent: GlintsTrackingEvent = {
    event_id: uuidv4(),
    properties: event.properties,
    event_time: moment().valueOf(),
    event_type: event.event_type,
  };

  const eventQueue = getEventQueue();
  setEventQueue([...eventQueue, trackingEvent]);
}

export function setExperimentationInfo(info: Record<string, unknown>) {
  if (!window?.GlintsTracking) return;

  const flatInfo = Object.entries(info).reduce(
    (acc, [key, value]) => {
      if (value && typeof value === 'object') {
        if ('variant' in value) {
          acc[key] = value.variant;
        } else if ('name' in value) {
          acc[key] = value.name;
        } else {
          acc[key] = value;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );

  const client = GlintsTrackingClient.getInstance();

  client.setExperimentationInfo({
    ...(client.getExperimentationInfo() ?? {}),
    ...flatInfo,
  });
}

export function getElementImpressionProperties(
  name: 'talent_impressions',
  id: string
) {
  return {
    'data-glints-tracking-view-element-name': name,
    'data-glints-tracking-view-element-id': id,
  };
}

function convertAttributeValue(
  value: string | null | unknown
): string | number | boolean | null | unknown {
  if (value === null || value === 'null') return null;
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  const numberValue = Number(value);
  if (!isNaN(numberValue)) {
    return numberValue;
  }
  if (isArray(value)) {
    return value.join(',');
  }
  return value;
}

export function getTrackingAttributes(
  element: HTMLElement
): Record<string, unknown> {
  const attributes = element.getAttributeNames() || [];

  const glintsTrackingAttributes = attributes
    .filter(
      attr =>
        attr.startsWith('data-glints-tracking-') &&
        !attr.startsWith('data-glints-tracking-element-name')
    )
    .reduce(
      (acc, attr) => {
        const modifiedAttr = snakeCase(
          attr.replace('data-glints-tracking-', '')
        );
        return {
          ...acc,
          [modifiedAttr]: convertAttributeValue(element.getAttribute(attr)),
        };
      },
      {} as Record<string, unknown>
    );

  const gtmAttributes = attributes
    .filter(attr => attr.startsWith('data-gtm-'))
    .reduce(
      (acc, attr) => {
        const modifiedAttr = snakeCase(attr.replace('data-gtm-', ''));
        return {
          ...acc,
          [modifiedAttr]: convertAttributeValue(element.getAttribute(attr)),
        };
      },
      {} as Record<string, unknown>
    );

  return {
    ...gtmAttributes,
    ...glintsTrackingAttributes,
  };
}
